import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faPencilAlt,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import useSiteData from '../../hooks/useSiteData';

const EditHomeStats = () => {
  const { data, isLoading: isSiteDataLoading, refetch } = useSiteData();
  const [formData, setFormData] = useState({
    items: [
      {
        number: 0,
        label: '',
        suffix: '',
        decimals: 0
      }
    ]
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Load initial data
  useEffect(() => {
    if (data?.stats) {
      setFormData({ items: data.stats });
    }
  }, [data]);

  const handleStatChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: field === 'number' || field === 'decimals' ? Number(value) : value
      };
      return { ...prev, items: newItems };
    });
  };

  const addStat = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { number: 0, label: '', suffix: '', decimals: 0 }]
    }));
  };

  const removeStat = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const docRef = doc(db, "stats", "singleton");
      await setDoc(docRef, formData);
      
      toast.success('Stats updated successfully!');
      setError('');
      setIsLoading(false);
      setIsEditing(false);
      setShowPreview(false);
      refetch();
    } catch (error) {
      console.error('Error updating stats:', error);
      setError('Failed to update stats.');
      toast.error('Failed to update stats.');
      setIsLoading(false);
    }
  };

  const renderReadOnlyMode = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Statistics</h2>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
          >
            <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
            Edit
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {formData.items.map((stat, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-primary mb-2">
                {stat.number}{stat.suffix}
              </div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6">Edit Statistics</h1>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6 flex items-center animate-fade-in">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
        
        {!showPreview ? (
          isEditing ? (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Edit Statistics</h2>
                  <button
                    onClick={addStat}
                    className="px-4 py-2 text-green-600 bg-green-50 rounded-md hover:bg-green-100 flex items-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add Stat
                  </button>
                </div>

                <div className="space-y-6">
                  {formData.items.map((stat, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium">Statistic #{index + 1}</h3>
                        <button
                          onClick={() => removeStat(index)}
                          className="text-red-500 hover:text-red-700"
                          disabled={formData.items.length === 1}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number
                          </label>
                          <input
                            type="number"
                            value={stat.number}
                            onChange={(e) => handleStatChange(index, 'number', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Label
                          </label>
                          <input
                            type="text"
                            value={stat.label}
                            onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Suffix (e.g., +, %, /5)
                          </label>
                          <input
                            type="text"
                            value={stat.suffix}
                            onChange={(e) => handleStatChange(index, 'suffix', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Decimal Places
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="2"
                            value={stat.decimals}
                            onChange={(e) => handleStatChange(index, 'decimals', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-4 sticky bottom-0 bg-gray-50 py-4">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  Cancel
                </button>
                <button
                  onClick={() => setShowPreview(true)}
                  className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Preview Changes
                </button>
              </div>
            </div>
          ) : (
            renderReadOnlyMode()
          )
        ) : (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Preview Changes
                </h3>
                <div className="mt-2 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {formData.items.map((stat, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-primary mb-2">
                          {stat.number}{stat.suffix}
                        </div>
                        <div className="text-gray-600">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Edit More
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {isLoading ? 'Saving...' : 'Confirm & Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditHomeStats;
